import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, TextField, Typography, InputAdornment, Autocomplete } from '@mui/material';
import { petsPathTo } from '../utils/Api';
import { PetsContext } from '../contexts/PetsContext';
import SnackbarContext from '../contexts/SnackbarContext';
import { TagsContext } from '../contexts/TagsContext';
import { useNavigate } from 'react-router-dom';
import FormBox from './building_blocks/FormBox';
import AvatarUploader from './AvatarUploader';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';


const PetDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { petsList, setPetsList } = useContext(PetsContext);
  const { openSnackbar } = useContext(SnackbarContext);
  const [pet, setPet] = useState(null);
  const { tags } = useContext(TagsContext);
  const speciesTags = tags.filter(tag => tag.category === 'species');
  const sizeTags = tags.filter(tag => tag.category === 'size');
  const ageGroupTags = tags.filter(tag => tag.category === 'age_group');
  const sexTags = tags.filter(tag => tag.category === 'sex');

  useEffect(() => {
    if (pet) {
      document.title = `${pet.name} - TudoPets.app`;
    }
  }, [pet]);

  useEffect(() => {
    setPet(petsList.find((pet) => pet.id === parseInt(id)));
  }, [petsList, id]);

  const handleFormFieldChange = (e) => {
    const { name, value } = e.target;
    setPet((prevPet) => ({
      ...prevPet,
      [name]: value,
    }));
  };

  const handleAvatarChange = (value) => {
    petsPathTo.update_avatar(id, {
      avatar: value
    }).then(result => {
      setPetsList(result.data);
      openSnackbar("Avatar atualizado com sucesso!", "success");
    })
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    petsPathTo.update(id, pet).then(result => {
      setPetsList(result.data);
      openSnackbar("Pet atualizado com sucesso!", "success");
    })
  }

  const handleRemovePet = () => {
    if (window.confirm("Tem certeza que deseja remover o pet?")) {
      petsPathTo.delete(id).then(result => {
        setPetsList(result.data);
        openSnackbar("Pet removido com sucesso!", "success");
        navigate('/mypets');
      })
    }
  }

  if (!pet) {
    return <div>Carregando...</div>;
  }

  return (
    <FormBox>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h4" gutterBottom>
          Detalhes do pet
        </Typography>
        <Box>
          <AvatarUploader handleAvatarChange={handleAvatarChange} pet={pet}/>
        </Box>
        <form>
          <TextField
            label="Nome"
            variant="outlined"
            value={pet.name || ''}
            name="name"
            fullWidth
            onChange={handleFormFieldChange}
            sx={{ mt: 2 }}
            autoComplete="off"
          />
          <Autocomplete
            options={speciesTags}
            getOptionLabel={(option) => option.name}
            value={speciesTags.find((tag) => tag.id === pet.species_tag_id) || null}
            name="species_tag_id"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Espécie"
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
              />
            )}
            onChange={(event, newValue) => {
              setPet((prevPet) => ({
                ...prevPet,
                species_tag_id: newValue ? newValue.id : null,
              }));
            }}
          />
          <Autocomplete
            options={sexTags}
            getOptionLabel={(option) => option.name}
            value={sexTags.find((tag) => tag.id === pet.sex_tag_id) || null}
            name="sex_tag_id"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sexo"
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
              />
            )}
            onChange={(event, newValue) => {
              setPet((prevPet) => ({
                ...prevPet,
                sex_tag_id: newValue ? newValue.id : null,
              }));
            }}
          />
          <TextField
            label="Raça"
            variant="outlined"
            value={pet.breed || ''}
            name="breed"
            fullWidth
            onChange={handleFormFieldChange}
            sx={{ mt: 2 }}
            autoComplete="off"
          />
          <Autocomplete
            options={ageGroupTags}
            getOptionLabel={(option) => option.name}
            value={ageGroupTags.find((tag) => tag.id === pet.age_group_tag_id) || null}
            name="age_group_tag_id"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Faixa de Idade"
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
              />
            )}
            onChange={(event, newValue) => {
              setPet((prevPet) => ({
                ...prevPet,
                age_group_tag_id: newValue ? newValue.id : null,
              }));
            }}
          />
          <DatePicker
            label="Data de Nascimento"
            variant="outlined"
            value={moment(pet.birth_date).isValid() ? moment(pet.birth_date) : null}
            name="birth_date"
            onChange={(e) => setPet(prevPet => ({...prevPet, birth_date: e}))}
            sx={{ mt: 2, width: '100%' }}
            format="DD/MM/YYYY"
          />
          <Autocomplete
            options={sizeTags}
            getOptionLabel={(option) => option.name}
            value={sizeTags.find((tag) => tag.id === pet.size_tag_id) || null}
            name="size_tag_id"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tamanho"
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
              />
            )}
            onChange={(event, newValue) => {
              setPet((prevPet) => ({
                ...prevPet,
                size_tag_id: newValue ? newValue.id : null,
              }));
            }}
          />
          <TextField
            label="Peso"
            variant="outlined"
            value={pet.weight || ''}
            name="weight"
            type="number"
            fullWidth
            onChange={handleFormFieldChange}
            sx={{ mt: 2 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">Kg</InputAdornment>,
            }}
          />
          <TextField
            label="Cor"
            variant="outlined"
            value={pet.color || ''}
            name="color"
            fullWidth
            onChange={handleFormFieldChange}
            sx={{ mt: 2 }}
            autoComplete="off"
          />
          <TextField
            label="Descrição"
            variant="outlined"
            value={pet.description || ''}
            name="description"
            fullWidth
            onChange={handleFormFieldChange}
            sx={{ mt: 2 }}
            autoComplete="off"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleRemovePet} color="error" variant="contained" sx={{ mx: 0, mt: 2 }}>
              Remover
            </Button>
            <Button type="submit" onClick={handleSubmit} variant="contained" sx={{ mx: 0, mt: 2 }}>
              Salvar
            </Button>
          </Box>
        </form>
      </Box>
    </FormBox>
  );
};

export default PetDetails;
